import { create } from 'zustand';
import type { HAR, HAREntry, HARStats, FilterState } from '@har-insight/parser';
import { parseHAR, filterEntries } from '@har-insight/parser';
import {
  generateHarHash,
  cacheInsights,
  getCachedInsights,
  queuePendingAnalysis,
  getPendingAnalyses,
  removePendingAnalysis,
  incrementRetryCount,
  clearOldCachedInsights,
} from '@/lib/ai-insights-db';

// AI Insight interface
export interface AIInsight {
  type: 'critical' | 'warning' | 'suggestion' | 'info';
  category: string;
  title: string;
  description: string;
  impact?: string;
  recommendation?: string;
  severity?: string;
}

// View mode for dashboard
type ViewMode = 'advanced' | 'basic';

// Tab interface for multiple HAR files
interface HARTab {
  id: string;
  fileName: string;
  har: HAR;
  stats: HARStats;
  filters: FilterState;
  filteredEntries: HAREntry[];
  selectedEntry: HAREntry | null;
  // AI Insights (per tab)
  aiInsights: AIInsight[] | null;
  aiInsightsLoading: boolean;
  aiInsightsError: string | null;
  aiInsightsCached: boolean; // Whether insights are from cache
  // Comparison data (per tab)
  compareHar: HAR | null;
  compareStats: HARStats | null;
  compareFileName: string | null;
  isCompareMode: boolean;
  // View mode preference (per tab)
  viewMode: ViewMode;
  // HAR content hash for cache lookup
  harHash?: string;
}

interface HARStore {
  // Tabs
  tabs: HARTab[];
  activeTabId: string | null;

  // Computed from active tab
  har: HAR | null;
  stats: HARStats | null;
  fileName: string | null;
  filters: FilterState;
  filteredEntries: HAREntry[];
  selectedEntry: HAREntry | null;
  aiInsights: AIInsight[] | null;
  aiInsightsLoading: boolean;
  aiInsightsError: string | null;
  aiInsightsCached: boolean;
  compareHar: HAR | null;
  compareStats: HARStats | null;
  compareFileName: string | null;
  isCompareMode: boolean;
  viewMode: ViewMode;

  // UI state
  isLoading: boolean;
  error: string | null;
  sidebarOpen: boolean;
  documentationOpen: boolean;
  isOnline: boolean;
  pendingAnalysesCount: number;

  // Tab actions
  addTab: (content: string, fileName: string) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;

  // Legacy actions (operate on active tab)
  loadHAR: (content: string, fileName: string) => void;
  loadCompareHAR: (content: string, fileName: string) => void;
  clearCompareHAR: () => void;
  setCompareMode: (enabled: boolean) => void;
  selectEntry: (entry: HAREntry | null) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  clearFilters: () => void;
  clearHAR: () => void;
  setSidebarOpen: (open: boolean) => void;
  setDocumentationOpen: (open: boolean) => void;
  setViewMode: (mode: ViewMode) => void;

  // AI Insights actions
  loadAIInsights: () => Promise<void>;
  clearAIInsights: () => void;
  retryPendingAnalyses: () => Promise<void>;
  setOnlineStatus: (isOnline: boolean) => void;
}

const defaultFilters: FilterState = {
  search: '',
  isRegex: false,
  methods: [],
  statusCodes: [],
  contentTypes: [],
  domains: [],
};

let tabIdCounter = 0;
const generateTabId = () => `tab-${++tabIdCounter}`;

// Helper to get active tab data
const getActiveTabData = (tabs: HARTab[], activeTabId: string | null): Partial<HARStore> => {
  const activeTab = tabs.find(t => t.id === activeTabId);
  if (!activeTab) {
    return {
      har: null,
      stats: null,
      fileName: null,
      filters: defaultFilters,
      filteredEntries: [],
      selectedEntry: null,
      aiInsights: null,
      aiInsightsLoading: false,
      aiInsightsError: null,
      aiInsightsCached: false,
      compareHar: null,
      compareStats: null,
      compareFileName: null,
      isCompareMode: false,
      viewMode: 'advanced' as ViewMode,
    };
  }
  return {
    har: activeTab.har,
    stats: activeTab.stats,
    fileName: activeTab.fileName,
    filters: activeTab.filters,
    filteredEntries: activeTab.filteredEntries,
    selectedEntry: activeTab.selectedEntry,
    aiInsights: activeTab.aiInsights,
    aiInsightsLoading: activeTab.aiInsightsLoading,
    aiInsightsError: activeTab.aiInsightsError,
    aiInsightsCached: activeTab.aiInsightsCached ?? false,
    compareHar: activeTab.compareHar,
    compareStats: activeTab.compareStats,
    compareFileName: activeTab.compareFileName,
    isCompareMode: activeTab.isCompareMode,
    viewMode: activeTab.viewMode as ViewMode,
  };
};

export const useHARStore = create<HARStore>((set, get) => ({
  // Initial state
  tabs: [],
  activeTabId: null,
      har: null,
      stats: null,
      fileName: null,
      filters: defaultFilters,
      filteredEntries: [],
      selectedEntry: null,
      aiInsights: null,
      aiInsightsLoading: false,
      aiInsightsError: null,
      aiInsightsCached: false,
      compareHar: null,
  compareStats: null,
  compareFileName: null,
  isCompareMode: false,
  viewMode: 'advanced',
  isLoading: false,
  error: null,
  sidebarOpen: false,
  documentationOpen: false,
  isOnline: navigator.onLine,
  pendingAnalysesCount: 0,

  // Add a new tab
  addTab: (content: string, fileName: string) => {
    set({ isLoading: true, error: null });

    try {
      const result = parseHAR(content);

      if (!result.success) {
        set({ isLoading: false, error: result.error });
        return;
      }

      const newTab: HARTab = {
        id: generateTabId(),
        fileName,
        har: result.data,
        stats: result.stats,
        filters: defaultFilters,
        filteredEntries: result.data.log.entries,
        selectedEntry: null,
        aiInsights: null,
        aiInsightsLoading: false,
        aiInsightsError: null,
        aiInsightsCached: false,
        compareHar: null,
        compareStats: null,
        compareFileName: null,
        isCompareMode: false,
        viewMode: 'advanced' as ViewMode,
      };

      const { tabs } = get();
      const newTabs = [...tabs, newTab];

      set({
        tabs: newTabs,
        activeTabId: newTab.id,
        ...getActiveTabData(newTabs, newTab.id),
        isLoading: false,
        error: null,
        sidebarOpen: false,
      });
    } catch (e) {
      set({
        isLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load HAR file',
      });
    }
  },

  // Close a tab
  closeTab: (tabId: string) => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.filter(t => t.id !== tabId);

    let newActiveTabId = activeTabId;
    if (activeTabId === tabId) {
      // Switch to another tab
      const closedIndex = tabs.findIndex(t => t.id === tabId);
      if (newTabs.length > 0) {
        newActiveTabId = newTabs[Math.min(closedIndex, newTabs.length - 1)].id;
      } else {
        newActiveTabId = null;
      }
    }

    set({
      tabs: newTabs,
      activeTabId: newActiveTabId,
      ...getActiveTabData(newTabs, newActiveTabId),
      sidebarOpen: false,
    });
  },

  // Set active tab
  setActiveTab: (tabId: string) => {
    const { tabs } = get();
    set({
      activeTabId: tabId,
      ...getActiveTabData(tabs, tabId),
      sidebarOpen: false,
    });
  },

  // Load HAR file (adds as new tab or replaces if no tabs)
  loadHAR: (content: string, fileName: string) => {
    get().addTab(content, fileName);
  },

  // Load comparison HAR file
  loadCompareHAR: (content: string, fileName: string) => {
    try {
      const result = parseHAR(content);

      if (!result.success) {
        set({ error: result.error });
        return;
      }

      const { tabs, activeTabId } = get();
      const newTabs = tabs.map(tab => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            compareHar: result.data,
            compareStats: result.stats,
            compareFileName: fileName,
            isCompareMode: true,
          };
        }
        return tab;
      });

      set({
        tabs: newTabs,
        ...getActiveTabData(newTabs, activeTabId),
        error: null,
      });
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : 'Failed to load comparison HAR file',
      });
    }
  },

  // Clear comparison HAR
  clearCompareHAR: () => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return {
          ...tab,
          compareHar: null,
          compareStats: null,
          compareFileName: null,
          isCompareMode: false,
        };
      }
      return tab;
    });

    set({
      tabs: newTabs,
      ...getActiveTabData(newTabs, activeTabId),
    });
  },

  // Toggle compare mode
  setCompareMode: (enabled: boolean) => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return { ...tab, isCompareMode: enabled };
      }
      return tab;
    });

    set({
      tabs: newTabs,
      isCompareMode: enabled,
    });
  },

  // Select entry
  selectEntry: (entry: HAREntry | null) => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return { ...tab, selectedEntry: entry };
      }
      return tab;
    });

    set({
      tabs: newTabs,
      selectedEntry: entry,
      sidebarOpen: entry !== null,
    });
  },

  // Update filters
  setFilters: (newFilters: Partial<FilterState>) => {
    const { tabs, activeTabId } = get();
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab) return;

    const updatedFilters = { ...activeTab.filters, ...newFilters };
    const filteredEntries = filterEntries(activeTab.har.log.entries, updatedFilters);

    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return { ...tab, filters: updatedFilters, filteredEntries };
      }
      return tab;
    });

    set({
      tabs: newTabs,
      filters: updatedFilters,
      filteredEntries,
    });
  },

  // Clear all filters
  clearFilters: () => {
    const { tabs, activeTabId } = get();
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab) return;

    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return {
          ...tab,
          filters: defaultFilters,
          filteredEntries: tab.har.log.entries,
        };
      }
      return tab;
    });

    set({
      tabs: newTabs,
      filters: defaultFilters,
      filteredEntries: activeTab.har.log.entries,
    });
  },

  // Clear all HAR data (close all tabs)
  clearHAR: () => {
    set({
      tabs: [],
      activeTabId: null,
      har: null,
      stats: null,
      fileName: null,
      filters: defaultFilters,
      filteredEntries: [],
      selectedEntry: null,
      aiInsights: null,
      aiInsightsLoading: false,
      aiInsightsError: null,
      compareHar: null,
      compareStats: null,
      compareFileName: null,
      isCompareMode: false,
      error: null,
      sidebarOpen: false,
    });
  },

  // Toggle sidebar
  setSidebarOpen: (open: boolean) => {
    if (!open) {
      const { tabs, activeTabId } = get();
      const newTabs = tabs.map(tab => {
        if (tab.id === activeTabId) {
          return { ...tab, selectedEntry: null };
        }
        return tab;
      });
      set({ tabs: newTabs, sidebarOpen: false, selectedEntry: null });
    } else {
      set({ sidebarOpen: true });
    }
  },

  setDocumentationOpen: (open: boolean) => set({ documentationOpen: open }),

  // Set view mode
  setViewMode: (mode: ViewMode) => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return { ...tab, viewMode: mode as ViewMode };
      }
      return tab;
    });
    set({
      tabs: newTabs,
      viewMode: mode,
    });
  },

  // Load AI Insights
  loadAIInsights: async () => {
    const { tabs, activeTabId, stats, filteredEntries, har } = get();
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab || !stats || !har) return;

    // Set loading state
    const newTabsLoading = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return { ...tab, aiInsightsLoading: true, aiInsightsError: null };
      }
      return tab;
    });
    set({ tabs: newTabsLoading, aiInsightsLoading: true, aiInsightsError: null });

    try {
      // Generate HAR hash for caching
      const harContent = JSON.stringify(har);
      const harHash = await generateHarHash(harContent);

      // Check if we have cached insights for this HAR
      const cached = await getCachedInsights(harHash);
      if (cached && cached.viewMode === activeTab.viewMode) {
        // Use cached insights
        const newTabsWithCachedInsights = tabs.map(tab => {
          if (tab.id === activeTabId) {
            return { 
              ...tab, 
              aiInsights: cached.insights, 
              aiInsightsLoading: false, 
              aiInsightsError: null,
              aiInsightsCached: true,
              harHash,
            };
          }
          return tab;
        });
        set({
          tabs: newTabsWithCachedInsights,
          aiInsights: cached.insights,
          aiInsightsLoading: false,
          aiInsightsError: null,
          aiInsightsCached: true,
        });

        // If offline, stop here - we showed cached data
        if (!navigator.onLine) {
          return;
        }
        // If online, continue to fetch fresh insights
      }

      // If offline and no cache, queue for later
      if (!navigator.onLine && !cached) {
        // Build summary for queuing
        const summary = {
          totalRequests: stats.totalRequests,
          totalSize: stats.totalSize,
          totalTime: stats.totalTime,
          avgResponseTime: filteredEntries.length > 0
            ? filteredEntries.reduce((sum, e) => sum + e.time, 0) / filteredEntries.length
            : 0,
          slowestRequests: [...filteredEntries]
            .sort((a, b) => b.time - a.time)
            .slice(0, 5)
            .map(e => ({ url: e.request.url.substring(0, 80), time: e.time })),
          largestRequests: [...filteredEntries]
            .sort((a, b) => (b.response.content.size || 0) - (a.response.content.size || 0))
            .slice(0, 5)
            .map(e => ({ url: e.request.url.substring(0, 80), size: e.response.content.size || 0 })),
          domains: Object.entries(stats.byDomain)
            .map(([domain, count]) => ({ domain, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5),
          methods: Object.entries(stats.byMethod)
            .map(([method, count]) => ({ method, count }))
            .sort((a, b) => b.count - a.count),
          statusCodes: Object.entries(stats.byStatus)
            .map(([code, count]) => ({ code: Number(code), count }))
            .sort((a, b) => b.count - a.count),
          errors: stats.errors,
          redirects: stats.redirects,
          securityHeaders: {
            missingCSP: filteredEntries.filter(e =>
              !e.response.headers.some(h => h.name.toLowerCase() === 'content-security-policy')
            ).length,
            missingHSTS: filteredEntries.filter(e =>
              !e.response.headers.some(h => h.name.toLowerCase() === 'strict-transport-security')
            ).length,
            missingXFrame: filteredEntries.filter(e =>
              !e.response.headers.some(h => h.name.toLowerCase() === 'x-frame-options')
            ).length,
            missingXContentType: filteredEntries.filter(e =>
              !e.response.headers.some(h => h.name.toLowerCase() === 'x-content-type-options')
            ).length,
          },
        };

        await queuePendingAnalysis(harHash, activeTab.fileName, summary, activeTab.viewMode);
        const pendingCount = (await getPendingAnalyses()).length;
        
        const newTabsWithError = tabs.map(tab => {
          if (tab.id === activeTabId) {
            return { 
              ...tab, 
              aiInsightsLoading: false, 
              aiInsightsError: 'You are offline. Analysis will run when you\'re back online.',
              harHash,
            };
          }
          return tab;
        });
        set({
          tabs: newTabsWithError,
          aiInsightsLoading: false,
          aiInsightsError: 'You are offline. Analysis will run when you\'re back online.',
          pendingAnalysesCount: pendingCount,
        });
        return;
      }

      // Build summary from stats and entries
      const summary = {
        totalRequests: stats.totalRequests,
        totalSize: stats.totalSize,
        totalTime: stats.totalTime,
        avgResponseTime: filteredEntries.length > 0
          ? filteredEntries.reduce((sum, e) => sum + e.time, 0) / filteredEntries.length
          : 0,
        slowestRequests: [...filteredEntries]
          .sort((a, b) => b.time - a.time)
          .slice(0, 5)
          .map(e => ({ url: e.request.url.substring(0, 80), time: e.time })),
        largestRequests: [...filteredEntries]
          .sort((a, b) => (b.response.content.size || 0) - (a.response.content.size || 0))
          .slice(0, 5)
          .map(e => ({ url: e.request.url.substring(0, 80), size: e.response.content.size || 0 })),
        domains: Object.entries(stats.byDomain)
          .map(([domain, count]) => ({ domain, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5),
        methods: Object.entries(stats.byMethod)
          .map(([method, count]) => ({ method, count }))
          .sort((a, b) => b.count - a.count),
        statusCodes: Object.entries(stats.byStatus)
          .map(([code, count]) => ({ code: Number(code), count }))
          .sort((a, b) => b.count - a.count),
        errors: stats.errors,
        redirects: stats.redirects,
        securityHeaders: {
          missingCSP: filteredEntries.filter(e =>
            !e.response.headers.some(h => h.name.toLowerCase() === 'content-security-policy')
          ).length,
          missingHSTS: filteredEntries.filter(e =>
            !e.response.headers.some(h => h.name.toLowerCase() === 'strict-transport-security')
          ).length,
          missingXFrame: filteredEntries.filter(e =>
            !e.response.headers.some(h => h.name.toLowerCase() === 'x-frame-options')
          ).length,
          missingXContentType: filteredEntries.filter(e =>
            !e.response.headers.some(h => h.name.toLowerCase() === 'x-content-type-options')
          ).length,
        },
      };

      const API_URL = import.meta.env.DEV
        ? 'http://localhost:8787'
        : (import.meta.env.VITE_API_URL || 'https://har-insight-api.thelazysre.workers.dev');

      const { viewMode } = get();
      const response = await fetch(`${API_URL}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary, viewMode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Analysis failed' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();
      const insights: AIInsight[] = data.insights || [];

      // Cache the insights
      await cacheInsights(harHash, activeTab.fileName, insights, viewMode);

      // Update state with insights
      const newTabsWithInsights = tabs.map(tab => {
        if (tab.id === activeTabId) {
          return { 
            ...tab, 
            aiInsights: insights, 
            aiInsightsLoading: false, 
            aiInsightsError: null,
            aiInsightsCached: false,
            harHash,
          };
        }
        return tab;
      });
      set({
        tabs: newTabsWithInsights,
        aiInsights: insights,
        aiInsightsLoading: false,
        aiInsightsError: null,
        aiInsightsCached: false,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze HAR data';
      const newTabsWithError = tabs.map(tab => {
        if (tab.id === activeTabId) {
          return { ...tab, aiInsightsLoading: false, aiInsightsError: errorMessage };
        }
        return tab;
      });
      set({
        tabs: newTabsWithError,
        aiInsightsLoading: false,
        aiInsightsError: errorMessage,
      });
    }
  },

  // Clear AI Insights
  clearAIInsights: () => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map(tab => {
      if (tab.id === activeTabId) {
        return { ...tab, aiInsights: null, aiInsightsError: null, aiInsightsCached: false };
      }
      return tab;
    });
    set({
      tabs: newTabs,
      aiInsights: null,
      aiInsightsError: null,
      aiInsightsCached: false,
    });
  },

  // Retry pending analyses when back online
  retryPendingAnalyses: async () => {
    const pending = await getPendingAnalyses();
    if (pending.length === 0) return;
    
    for (const analysis of pending) {
      try {
        const API_URL = import.meta.env.DEV
          ? 'http://localhost:8787'
          : (import.meta.env.VITE_API_URL || 'https://har-insight-api.thelazysre.workers.dev');

        const response = await fetch(`${API_URL}/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ summary: analysis.summary, viewMode: analysis.viewMode }),
        });

        if (response.ok) {
          const data = await response.json();
          const insights: AIInsight[] = data.insights || [];
          
          // Cache the results
          await cacheInsights(analysis.id, analysis.fileName, insights, analysis.viewMode);
          
          // Remove from pending
          await removePendingAnalysis(analysis.id);
        } else {
          // Increment retry count
          await incrementRetryCount(analysis.id);
        }
      } catch {
        // Increment retry count on error
        await incrementRetryCount(analysis.id);
      }
    }

    // Update pending count
    const pendingCount = (await getPendingAnalyses()).length;
    set({ pendingAnalysesCount: pendingCount });

    // Clean up old cached entries
    await clearOldCachedInsights();
  },

  // Set online status
  setOnlineStatus: (isOnline: boolean) => {
    set({ isOnline });
    
    // If coming back online, retry pending analyses
    if (isOnline) {
      get().retryPendingAnalyses();
    }
  },
}));

// Empty array constant for stable references
const EMPTY_ARRAY: string[] = [];

// Selectors - return stable references to prevent infinite re-renders
export const selectDomains = (state: HARStore) => {
  if (!state.stats) return EMPTY_ARRAY;
  const domains = Object.keys(state.stats.byDomain);
  if (domains.length === 0) return EMPTY_ARRAY;
  return domains.sort((a, b) => state.stats!.byDomain[b] - state.stats!.byDomain[a]);
};

export const selectMethods = (state: HARStore) => {
  if (!state.stats) return EMPTY_ARRAY;
  const methods = Object.keys(state.stats.byMethod);
  if (methods.length === 0) return EMPTY_ARRAY;
  return methods.sort();
};

export const selectContentTypes = (state: HARStore) => {
  if (!state.stats) return EMPTY_ARRAY;
  const types = Object.keys(state.stats.byContentType);
  if (types.length === 0) return EMPTY_ARRAY;
  return types.sort();
};
